import { css } from "@repo/styled-system/css";

/** Shared presentational class names for the bookings surfaces (kept in one place, css-token based). */

export const modalOverlay = css({
  position: "fixed",
  inset: "0",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  bg: "black/40",
  zIndex: "40",
  p: "4",
  overflowY: "auto",
});

export const modalPanel = css({
  // A native `<dialog open>` is UA-styled `position: absolute`, which drops it out of the overlay's
  // flex flow so `justifyContent: center` is ignored and it sticks to the left. Force it back into
  // normal flow so the overlay centers it.
  position: "static",
  bg: "bg.default",
  rounded: "lg",
  borderWidth: "1px",
  p: "5",
  maxW: "xl",
  width: "full",
  mt: "8",
  mb: "8",
  display: "flex",
  flexDirection: "column",
  gap: "3",
  outline: "none",
});

export const fieldLabel = css({
  display: "flex",
  flexDirection: "column",
  gap: "1",
  fontSize: "sm",
  fontWeight: "medium",
});

export const textInput = css({
  px: "3",
  py: "2",
  rounded: "md",
  borderWidth: "1px",
  borderColor: "border.default",
  bg: "bg.default",
  fontSize: "sm",
  fontWeight: "normal",
  _focusVisible: {
    outlineWidth: "2px",
    outlineColor: "colorPalette.default",
    outlineOffset: "1px",
  },
});

/**
 * Native date/time inputs styled to match the design system: same border/radius/height as `textInput`,
 * with the picker indicator tinted to the accent and a themed focus ring — so `<input type="date">`
 * and `<input type="time">` stop clashing with the themed controls around them.
 */
export const dateTimeInput = css({
  px: "3",
  py: "2",
  rounded: "md",
  borderWidth: "1px",
  borderColor: "border.default",
  bg: "bg.default",
  color: "fg.default",
  fontSize: "sm",
  fontWeight: "medium",
  colorScheme: "light dark",
  minH: "9",
  _hover: { borderColor: "border.emphasized" },
  _focusVisible: {
    outlineWidth: "2px",
    outlineColor: "colorPalette.default",
    outlineOffset: "1px",
    borderColor: "colorPalette.default",
  },
  "&::-webkit-calendar-picker-indicator": {
    cursor: "pointer",
    opacity: "0.6",
    _hover: { opacity: "1" },
  },
});

export const primaryButton = css({
  px: "4",
  py: "2",
  rounded: "md",
  bg: "colorPalette.default",
  color: "colorPalette.fg",
  fontSize: "sm",
  fontWeight: "medium",
  _disabled: { opacity: "0.5", cursor: "not-allowed" },
});

export const secondaryButton = css({
  px: "3",
  py: "2",
  rounded: "md",
  borderWidth: "1px",
  borderColor: "border.default",
  fontSize: "sm",
  _disabled: { opacity: "0.5" },
});

export const dangerButton = css({
  px: "4",
  py: "2",
  rounded: "md",
  bg: "booking.noShow.solid",
  color: "white",
  fontSize: "sm",
  fontWeight: "medium",
  _disabled: { opacity: "0.5", cursor: "not-allowed" },
});

const bannerBase = css({
  p: "3",
  rounded: "md",
  borderWidth: "1px",
  fontSize: "sm",
  display: "flex",
  flexDirection: "column",
  gap: "2",
});

// Literal per-kind classes so Panda emits the CSS (no computed token strings).
const bannerKind = {
  warn: css({
    bg: "booking.pending.bg",
    color: "booking.pending.fg",
    borderColor: "booking.pending.border",
  }),
  error: css({
    bg: "booking.noShow.bg",
    color: "booking.noShow.fg",
    borderColor: "booking.noShow.border",
  }),
  info: css({ bg: "bg.subtle", color: "fg.default", borderColor: "border.default" }),
  review: css({
    bg: "booking.review.bg",
    color: "booking.review.fg",
    borderColor: "booking.review.border",
  }),
} as const;

export function banner(kind: keyof typeof bannerKind): string {
  return `${bannerBase} ${bannerKind[kind]}`;
}
